import { msg } from "@lingui/core/macro"
import type { MessageDescriptor } from "@lingui/core"
import type { I18n } from "@lingui/core"
import type {
  ConversationMessageContentType,
  ConversationThreadEntry,
} from "@/types/v2/conversation"

// "TODAY", "YESTERDAY", or e.g. "MAR 14" / "MAR 14, 2024"
// (older than this year). Uppercase because the design uses
// caps for day separators.
//
// This is the inbox-flavor bucketer — TODAY / YESTERDAY then
// straight to month-day. The v2 deal history feed uses
// `bucketLabel` instead, which additionally surfaces the day
// name ("MONDAY", "WEDNESDAY") for dates within the last 7
// days. Keep this function's behavior stable so the inbox
// transcript headers stay unchanged.
export function dayLabel(
  iso: string,
  now: Date = new Date(),
): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""

  const isSameDay = sameDay(date, now)
  if (isSameDay) return "TODAY"

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, yesterday)) return "YESTERDAY"

  const formatted = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() === now.getFullYear()
        ? undefined
        : "numeric",
  })
  return formatted.toUpperCase()
}

// Date-bucket label for v2 history feeds (deal history,
// future contact/company history). Extends `dayLabel`'s
// rule set with a "within last 7 days → day name" layer.
//
// Rule sheet (Designer brief, CON-89):
//   - Today                       → "TODAY"
//   - Yesterday                   → "YESTERDAY"
//   - Within last 7 calendar days → "MONDAY" / "WEDNESDAY"
//   - Same calendar year          → "APR 26"
//   - Different calendar year     → "APR 26, 2025"
//
// The "last 7 days" window is *exclusive of today and
// yesterday* — those are already covered by their own
// labels — and walks back 7 days from `now`. A date that
// falls exactly 7 days ago drops out of the day-name
// window and renders as a month-day instead. That matches
// the brief's "within last 7 days" copy without
// double-labeling a Monday as both "MONDAY" and a date.
export function bucketLabel(
  iso: string,
  now: Date = new Date(),
): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""

  if (sameDay(date, now)) return "TODAY"

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, yesterday)) return "YESTERDAY"

  // Day-name window: 2..6 days ago. (`yesterday` is 1
  // day ago and already handled above; day 7 is excluded
  // so a date never collides with its own weekday from a
  // week earlier.)
  for (let offset = 2; offset <= 6; offset++) {
    const candidate = new Date(now)
    candidate.setDate(now.getDate() - offset)
    if (sameDay(date, candidate)) {
      return date
        .toLocaleDateString("en-US", { weekday: "long" })
        .toUpperCase()
    }
  }

  const formatted = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() === now.getFullYear()
        ? undefined
        : "numeric",
  })
  return formatted.toUpperCase()
}

// Whether a row under the given bucket label should show
// a per-row clock time. Per the designer brief (rule D):
//
//   - Under TODAY / YESTERDAY → show clock time
//   - Under any date bucket  → no per-row timestamp
//     (the bucket header is enough; repeating the date
//     on the row would just duplicate it)
//
// Centralized here so the deal history feed, the new
// `/v2/deals/:id/history` page, and any future v2 feed
// that adopts these buckets all share the same rule.
export function bucketShowsClockTime(label: string): boolean {
  return label === "TODAY" || label === "YESTERDAY"
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

// Compact relative-time label for headers and small
// metadata rows: "just now" / "5 min ago" / "2 hr ago"
// / "Yesterday" / "Apr 27".
//
// The AI PAUSED banner uses this for its "12 min ago"
// suffix (Paper 7DD-0); the History feed event rows use
// it for the same suffix on TODAY / YESTERDAY entries.
// The intent is short — anything beyond a day collapses
// to a date so the line stays readable.
export function relativeTimeLabel(
  iso: string,
  now: Date = new Date(),
): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""

  const seconds = Math.round((now.getTime() - date.getTime()) / 1000)
  if (seconds < 45) return "just now"

  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.round(minutes / 60)
  if (hours < 24 && sameDay(date, now)) return `${hours} hr ago`

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, yesterday)) return "Yesterday"

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() === now.getFullYear()
        ? undefined
        : "numeric",
  })
}

// Short clock time, e.g. "9:42 AM". The design favors the
// short, no-leading-zero form.
export function clockLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
}

// Groups consecutive thread entries (messages AND calls,
// interleaved by time — CON-180) by their day separator,
// preserving original order. Each group's key is the day
// label and its items are the entries that share it. The day
// label is derived from each entry's `occurredAt` so a call
// and a message on the same day fall under one divider.
export interface ThreadEntryDayGroup {
  key: string
  label: string
  entries: ConversationThreadEntry[]
}

export function groupThreadEntriesByDay(
  entries: ConversationThreadEntry[],
  now: Date = new Date(),
): ThreadEntryDayGroup[] {
  const groups: ThreadEntryDayGroup[] = []

  for (const entry of entries) {
    const label = dayLabel(entry.occurredAt, now)
    const last = groups.at(-1)
    if (last && last.label === label) {
      last.entries.push(entry)
    } else {
      groups.push({
        // Key on the timeline_entry id — globally unique
        // across message/call kinds.
        key: `${label}-${entry.id}`,
        label,
        entries: [entry],
      })
    }
  }

  return groups
}

const FALLBACK_LABELS: Record<
  ConversationMessageContentType,
  MessageDescriptor
> = {
  text: msg`Message`,
  image: msg`Photo`,
  video: msg`Video`,
  audio: msg`Voice message`,
  document: msg`Document`,
  voice_call: msg`Voice call`,
  sticker: msg`Sticker`,
  location: msg`Location`,
  contacts: msg`Contact card`,
  other: msg`Message`,
}

// What to render in a bubble when there's no text content
// (e.g. images with no caption, voice notes, locations).
export function contentTypeLabel(
  contentType: ConversationMessageContentType,
  i18n: I18n,
): string {
  const descriptor = FALLBACK_LABELS[contentType] ?? msg`Message`
  return i18n._(descriptor)
}
